import { useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, ArrowRight, Briefcase, Clock, MapPin, Upload, Check, AlertCircle, IndianRupee } from 'lucide-react'
import Seo from '../components/Seo.jsx'
import { JobStructuredData, BreadcrumbStructuredData } from '../components/StructuredData.jsx'
import PhoneField from '../components/PhoneField.jsx'

/* =========================================================================
   JobDetail — /careers/:slug

   The role in full, followed by the application form. Two screening questions
   and a resume, which is all we ask for at this stage.

   The form is deliberately on the same page rather than behind another click:
   a candidate who has read to the bottom is already the one we want to hear
   from, and sending them somewhere else is where applications get lost.
   ========================================================================= */

const API_BASE = import.meta.env.VITE_API_BASE || '/api'

const MIN_ANSWER = 40

function Field({ label, hint, children, error }) {
  return (
    <label className="block">
      <span className="flex items-baseline justify-between gap-3">
        <span className="text-[13px] font-bold text-slate-800">{label}</span>
        {hint && <span className="text-[11px] font-medium text-slate-400">{hint}</span>}
      </span>
      <div className="mt-1.5">{children}</div>
      {error && (
        <span className="mt-1.5 flex items-center gap-1.5 text-[12px] font-medium text-rose-600">
          <AlertCircle size={13} /> {error}
        </span>
      )}
    </label>
  )
}

/* 16px on mobile: anything smaller and iOS Safari zooms the viewport on
   focus, which throws the reader out of the form they are filling in. */
const input =
  'block w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-[16px] text-slate-900 ' +
  'placeholder:text-slate-400 outline-none transition-all focus:border-emerald-600 focus:ring-2 ' +
  'focus:ring-emerald-600/20 sm:py-3 sm:text-[14px]'

function Bullets({ items }) {
  if (!items?.length) return null
  return (
    <ul className="mt-4 space-y-2.5">
      {items.map((line) => (
        <li key={line} className="flex items-start gap-3 text-[15px] leading-relaxed text-slate-600">
          <span className="mt-[7px] size-1.5 shrink-0 rounded-full bg-emerald-600" />
          {line}
        </li>
      ))}
    </ul>
  )
}

export default function JobDetail() {
  const { slug } = useParams()

  const [job, setJob] = useState(null)
  const [others, setOthers] = useState([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const [form, setForm] = useState({ name: '', email: '', phone: '', why_hire: '', why_us: '' })
  const [resume, setResume] = useState(null)
  const [errors, setErrors] = useState({})
  const [status, setStatus] = useState('idle') // idle | submitting | success | error
  const [serverMsg, setServerMsg] = useState('')
  const fileRef = useRef(null)
  const formRef = useRef(null)

  useEffect(() => {
    let live = true
    setLoading(true)
    setNotFound(false)
    fetch(`${API_BASE}/get-job.php?slug=${encodeURIComponent(slug)}`)
      .then((r) => r.json())
      .then((d) => {
        if (!live) return
        if (d.success && d.job) {
          setJob(d.job)
          setOthers(d.others || [])
        } else {
          setNotFound(true)
        }
        setLoading(false)
      })
      .catch(() => {
        if (!live) return
        setNotFound(true)
        setLoading(false)
      })
    return () => {
      live = false
    }
  }, [slug])

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [slug])

  const update = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }))
    setErrors((x) => ({ ...x, [e.target.name]: '' }))
  }

  const pickFile = (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    const ext = f.name.split('.').pop()?.toLowerCase()
    if (!['pdf', 'doc', 'docx'].includes(ext)) {
      setErrors((x) => ({ ...x, resume: 'PDF, DOC or DOCX only.' }))
      setResume(null)
      return
    }
    if (f.size > 5 * 1024 * 1024) {
      setErrors((x) => ({ ...x, resume: 'Please keep it under 5MB.' }))
      setResume(null)
      return
    }
    setErrors((x) => ({ ...x, resume: '' }))
    setResume(f)
  }

  /* Checked here as well as on the server so the candidate sees the problem
     next to the field rather than as one line after a round trip. */
  const validate = () => {
    const e = {}
    if (!form.name.trim()) e.name = 'We need a name to put to the application.'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'That email does not look right.'
    if (form.why_hire.trim().length < MIN_ANSWER) e.why_hire = `A bit more detail, please. At least ${MIN_ANSWER} characters.`
    if (form.why_us.trim().length < MIN_ANSWER) e.why_us = `A bit more detail, please. At least ${MIN_ANSWER} characters.`
    if (!resume) e.resume = 'Please attach your resume.'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) {
      formRef.current?.querySelector('[data-invalid="true"]')?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      return
    }
    setStatus('submitting')
    setServerMsg('')

    const body = new FormData()
    body.append('job_id', job.id)
    Object.entries(form).forEach(([k, v]) => body.append(k, v.trim()))
    body.append('resume', resume)

    try {
      const res = await fetch(`${API_BASE}/apply-job.php`, { method: 'POST', body })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.success) {
        setStatus('success')
        setServerMsg(data.message || '')
      } else {
        setStatus('error')
        setServerMsg(data.message || 'Something went wrong. Please try again.')
      }
    } catch {
      setStatus('error')
      setServerMsg('Could not reach the server. Please try again in a moment.')
    }
  }

  if (loading) {
    return (
      <main className="min-h-screen bg-white pt-28">
        <div className="mx-auto max-w-3xl px-6">
          <div className="h-4 w-28 animate-pulse rounded bg-slate-100" />
          <div className="mt-5 h-10 w-3/4 animate-pulse rounded bg-slate-100" />
          <div className="mt-3 h-4 w-1/2 animate-pulse rounded bg-slate-100" />
          <div className="mt-10 space-y-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-4 animate-pulse rounded bg-slate-100" style={{ width: `${92 - i * 7}%` }} />
            ))}
          </div>
        </div>
      </main>
    )
  }

  if (notFound || !job) {
    return (
      <main className="grid min-h-screen place-items-center bg-white px-6 pt-20 text-center">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">That role is no longer listed</h1>
          <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-500">
            It may have been filled or taken down. The current openings are all on the careers page.
          </p>
          <Link
            to="/careers"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-emerald-700 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-emerald-600"
          >
            <ArrowLeft size={16} /> Back to careers
          </Link>
        </div>
      </main>
    )
  }

  const isOpen = Number(job.is_open) === 1
  const meta = [
    { icon: Briefcase, v: job.type },
    { icon: MapPin, v: job.location },
    { icon: Clock, v: job.experience },
    job.salary_range ? { icon: IndianRupee, v: job.salary_range } : null,
  ].filter((m) => m && m.v)

  return (
    <main className="min-h-screen bg-white pt-20">
      <Seo
        title={`${job.title} — Careers at Spectranox`}
        description={job.summary || `We are hiring a ${job.title} at Spectranox.`}
        path={`/careers/${job.slug}`}
      />
      <JobStructuredData
        title={job.title}
        description={job.description || job.summary || `We are hiring a ${job.title} at Spectranox.`}
        path={`/careers/${job.slug}`}
        location={job.location}
        employmentType={job.type}
        datePosted={job.created_at ? String(job.created_at).slice(0, 10) : undefined}
      />
      <BreadcrumbStructuredData
        trail={[
          { name: 'Home', path: '/' },
          { name: 'Careers', path: '/careers' },
          { name: job.title, path: `/careers/${job.slug}` },
        ]}
      />

      {/* ---------------- header ---------------- */}
      <section className="relative overflow-hidden bg-[#0A0C10] text-white">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #34d399 1px, transparent 0)',
            backgroundSize: '32px 32px',
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 right-[-8%] h-80 w-[36rem] rounded-full blur-[130px]"
          style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.16), transparent 62%)' }}
        />

        <div className="relative mx-auto max-w-4xl px-6 py-12 sm:py-16 lg:px-8">
          <Link
            to="/careers"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-400 transition-colors hover:text-emerald-400"
          >
            <ArrowLeft size={15} /> All openings
          </Link>

          <div className="mt-6 flex flex-wrap items-center gap-2.5">
            <span className="rounded-full bg-emerald-400/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-400 ring-1 ring-emerald-400/25">
              {job.department}
            </span>
            {!isOpen && (
              <span className="rounded-full bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-300">
                Closed
              </span>
            )}
          </div>

          <h1 className="mt-4 font-display text-[clamp(1.9rem,4.6vw,3rem)] font-bold leading-[1.08] tracking-tight">
            {job.title}
          </h1>

          {job.summary && (
            <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-slate-300 sm:text-base">{job.summary}</p>
          )}

          <div className="mt-7 flex flex-wrap gap-x-6 gap-y-3">
            {meta.map(({ icon: I, v }) => (
              <span key={v} className="inline-flex items-center gap-2 text-[13px] font-medium text-slate-300">
                <I size={15} className="text-emerald-400" /> {v}
              </span>
            ))}
          </div>

          {isOpen && (
            <a
              href="#apply"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-emerald-700 px-6 py-3 text-sm font-bold text-white transition-all hover:bg-emerald-600"
            >
              Apply for this role <ArrowRight size={16} />
            </a>
          )}
        </div>
      </section>

      {/* ---------------- body ---------------- */}
      <section className="mx-auto max-w-4xl px-6 py-14 lg:px-8 lg:py-20">
        {job.description && (
          <div className="max-w-2xl">
            <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">About the role</h2>
            {job.description.split(/\n{2,}/).map((para) => (
              <p key={para.slice(0, 40)} className="mt-4 text-[15px] leading-relaxed text-slate-600">
                {para}
              </p>
            ))}
          </div>
        )}

        <div className="mt-12 grid gap-10 sm:grid-cols-2 sm:gap-8">
          {job.responsibilities_list?.length > 0 && (
            <div>
              <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">What you will do</h2>
              <Bullets items={job.responsibilities_list} />
            </div>
          )}
          {job.requirements_list?.length > 0 && (
            <div>
              <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">What we are looking for</h2>
              <Bullets items={job.requirements_list} />
            </div>
          )}
        </div>
      </section>

      {/* ---------------- apply ---------------- */}
      <section id="apply" className="scroll-mt-24 bg-slate-50 py-14 lg:py-20">
        {/* Full bleed on mobile: the form is the whole point of the page, and a
            card floating inside gutters wastes the width a phone has. */}
        <div className="mx-auto max-w-3xl px-0 sm:px-6 lg:px-8">
          <div className="bg-white px-6 py-10 shadow-[0_18px_40px_-28px_rgba(15,23,42,0.35)] ring-1 ring-black/5 sm:rounded-3xl sm:px-10 sm:py-12">
            {!isOpen ? (
              <div className="text-center">
                <h2 className="font-display text-2xl font-bold text-slate-900">This role has closed</h2>
                <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-500">
                  We are not taking applications for it any more. The openings below are live.
                </p>
              </div>
            ) : status === 'success' ? (
              <div className="py-8 text-center">
                <span className="mx-auto grid size-14 place-items-center rounded-full bg-emerald-700 text-white">
                  <Check size={28} strokeWidth={2.6} />
                </span>
                <h2 className="mt-5 font-display text-2xl font-bold text-slate-900">Application received</h2>
                <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-600">
                  {serverMsg} A hiring lead reads every application, and you will hear back either way,
                  usually within two working days.
                </p>
                <Link
                  to="/careers"
                  className="mt-7 inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
                >
                  See the other openings
                </Link>
              </div>
            ) : (
              <>
                <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">
                  Apply for {job.title}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">
                  Two questions and your resume. No cover letter, no login, and nothing else to fill in.
                </p>

                <form ref={formRef} onSubmit={onSubmit} className="mt-8 space-y-5" noValidate>
                  <div data-invalid={!!errors.name}>
                    <Field label="Full name" error={errors.name}>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={update}
                        placeholder="Your name"
                        autoComplete="name"
                        autoCapitalize="words"
                        enterKeyHint="next"
                        className={input}
                      />
                    </Field>
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div data-invalid={!!errors.email}>
                      <Field label="Email" error={errors.email}>
                        <input
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={update}
                          placeholder="you@example.com"
                          autoComplete="email"
                          inputMode="email"
                          autoCapitalize="none"
                          autoCorrect="off"
                          spellCheck="false"
                          enterKeyHint="next"
                          className={input}
                        />
                      </Field>
                    </div>
                    <Field label="Phone" hint="Optional">
                      <PhoneField
                        name="phone"
                        value={form.phone}
                        onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
                        placeholder="Phone number"
                        ariaLabel="Phone number"
                      />
                    </Field>
                  </div>

                  <div data-invalid={!!errors.why_hire}>
                    <Field
                      label="Why should we hire you?"
                      hint={`${form.why_hire.trim().length}/${MIN_ANSWER}`}
                      error={errors.why_hire}
                    >
                      <textarea
                        name="why_hire"
                        rows={5}
                        value={form.why_hire}
                        onChange={update}
                        placeholder="What you would bring to this role, and something you have built that shows it."
                        className={input + ' resize-none'}
                      />
                    </Field>
                  </div>

                  <div data-invalid={!!errors.why_us}>
                    <Field
                      label="Why do you want to work with us?"
                      hint={`${form.why_us.trim().length}/${MIN_ANSWER}`}
                      error={errors.why_us}
                    >
                      <textarea
                        name="why_us"
                        rows={5}
                        value={form.why_us}
                        onChange={update}
                        placeholder="What drew you to Spectranox specifically, rather than to any job."
                        className={input + ' resize-none'}
                      />
                    </Field>
                  </div>

                  <div data-invalid={!!errors.resume}>
                    <Field label="Resume" hint="PDF, DOC or DOCX · under 5MB" error={errors.resume}>
                      <input
                        ref={fileRef}
                        type="file"
                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onChange={pickFile}
                        className="sr-only"
                      />
                      <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        className={
                          'flex w-full items-center gap-3 rounded-xl border border-dashed px-4 py-4 text-left transition-colors ' +
                          (resume
                            ? 'border-emerald-600/40 bg-emerald-50/60'
                            : 'border-slate-300 bg-white hover:border-emerald-600/50 hover:bg-slate-50')
                        }
                      >
                        <span
                          className={
                            'grid size-10 shrink-0 place-items-center rounded-lg ' +
                            (resume ? 'bg-emerald-700 text-white' : 'bg-slate-100 text-slate-500')
                          }
                        >
                          {resume ? <Check size={18} strokeWidth={2.6} /> : <Upload size={18} />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-slate-800">
                            {resume ? resume.name : 'Choose a file'}
                          </span>
                          <span className="block text-[12px] text-slate-500">
                            {resume ? `${(resume.size / 1024 / 1024).toFixed(2)} MB · tap to replace` : 'PDF, DOC or DOCX'}
                          </span>
                        </span>
                      </button>
                    </Field>
                  </div>

                  {status === 'error' && (
                    <p role="alert" className="flex items-start gap-2 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700">
                      <AlertCircle size={16} className="mt-0.5 shrink-0" /> {serverMsg}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="btn-on-solid inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-700 px-6 py-4 text-[16px] font-bold text-white transition-all hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-70 sm:py-3.5 sm:text-[15px]"
                  >
                    {status === 'submitting' ? 'Sending your application…' : 'Submit application'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ---------------- other roles ---------------- */}
      {others.length > 0 && (
        <section className="mx-auto max-w-4xl px-6 py-14 lg:px-8 lg:py-16">
          <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">Other openings</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {others.map((o, i) => (
              <motion.div
                key={o.id}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
              >
                <Link
                  to={`/careers/${o.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:border-emerald-600/40 hover:shadow-[0_10px_28px_-18px_rgba(4,120,87,0.4)]"
                >
                  <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">
                    {o.department}
                  </span>
                  <span className="mt-2 font-display text-lg font-bold tracking-tight text-slate-900 transition-colors group-hover:text-emerald-700">
                    {o.title}
                  </span>
                  <span className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[12px] font-medium text-slate-500">
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin size={13} className="text-slate-400" /> {o.location}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock size={13} className="text-slate-400" /> {o.experience}
                    </span>
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}
    </main>
  )
}
